/* NORDAEN — motion runtime.
   Three jobs: reveal on entry, hold hero video politely, and arm
   hover-video panels. No dependencies. Degrades to "everything is
   simply visible, nothing moves". */

(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  /* Respect the user's data preferences as well as their motion ones.
     Save-Data and 2g/slow-2g mean: still images are the whole experience. */
  const thrifty = () => {
    const c = navigator.connection;
    if (!c) return false;
    return c.saveData === true || /(^|-)2g$/.test(c.effectiveType || '');
  };

  /* ---- Reveal ------------------------------------------------ */
  const reveal = () => {
    const items = document.querySelectorAll('[data-n-reveal]:not(.is-in)');
    if (!items.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      // Fire as soon as any part enters. A 12% threshold meant a tall
      // frame had to be most of the way on screen before it appeared, which
      // read as an empty section rather than as a considered reveal.
      { rootMargin: '0px 0px -4% 0px', threshold: 0 }
    );
    items.forEach((el) => io.observe(el));
  };

  /* Safety net. If for any reason the observer never reports — a layout
     quirk, a clipped ancestor, a browser oddity — nothing should stay
     invisible. After a short grace period every remaining item is shown.
     Content must never depend on an animation succeeding. */
  const failsafe = () => {
    if (failsafe.armed) return;
    failsafe.armed = true;

    const sweep = () => {
      document
        .querySelectorAll('[data-n-reveal]:not(.is-in)')
        .forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.top < innerHeight && r.bottom > 0) el.classList.add('is-in');
        });
    };

    // Horizon scrolls an inner .page-wrapper, so listen there as well as on
    // the window; whichever is real will drive the sweep.
    const scroller = document.querySelector('.page-wrapper') || window;
    let queued = false;
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; sweep(); });
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    setTimeout(sweep, 2000);
  };

  /* ---- Hero video -------------------------------------------
     Plays only while on screen. Autoplay rejection is expected on
     some devices; the poster stays and we do not retry noisily. */
  const heroVideo = () => {
    const vids = document.querySelectorAll('video[data-n-video]');
    if (!vids.length) return;

    const allowed = !reduced.matches && !thrifty();

    if (!('IntersectionObserver' in window)) {
      if (allowed) vids.forEach((v) => v.play().catch(() => {}));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          // data-n-video="once" plays a single time and keeps its last frame.
          // It still pauses off-screen to save decode work, but resumes rather
          // than restarting, and never replays after it has ended.
          const once = target.dataset.nVideo === 'once';

          if (isIntersecting && allowed) {
            if (once && target.ended) return;
            target.play().catch(() => {});
          } else if (!target.paused) {
            target.pause();
          }
        });
      },
      { threshold: 0.15 }
    );
    vids.forEach((v) => io.observe(v));

    // Reveal each video only once it is genuinely playing, so a slow
    // connection shows the still rather than a black rectangle.
    //
    // Two marks, because there are two shapes of this. A hero or a contact
    // panel has one video filling the whole section, so its CSS keys off the
    // section. A story frame or an object has several videos in one section,
    // each stacked over its own still, so those key off the element itself.
    vids.forEach((v) => {
      v.addEventListener('playing', () => {
        v.classList.add('is-playing');
        const host = v.closest('section');
        if (host) host.classList.add('is-playing');
      });
    });
  };

  /* ---- Hover video ------------------------------------------
     A panel marked [data-n-hover] holds a still and a muted video.
     The video carries preload="none", so nothing is fetched until
     the pointer enters the surrounding group — that warms it — and
     playback begins only on the panel itself. The video is revealed
     only once it is genuinely playing, so a slow network shows the
     still rather than a black rectangle.

     Touch devices never trigger this: there is no hover to read, and
     two autoplaying videos is not a thing to do to a phone. */
  const hoverVideo = () => {
    const panels = document.querySelectorAll('[data-n-hover]');
    if (!panels.length) return;

    const canHover = matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!canHover || reduced.matches || thrifty()) return;

    let warmed = false;
    const warm = () => {
      if (warmed) return;
      warmed = true;
      panels.forEach((p) => {
        const v = p.querySelector('video');
        if (v && v.preload === 'none') v.preload = 'auto';
      });
    };

    // Warm on first intent anywhere in the group.
    const group = panels[0].closest('[data-n-hover-group]') || document.body;
    group.addEventListener('pointerenter', warm, { once: true });
    group.addEventListener('focusin', warm, { once: true });

    panels.forEach((panel) => {
      const video = panel.querySelector('video');
      if (!video) return;

      video.addEventListener('playing', () => panel.classList.add('is-playing'));

      const enter = () => {
        warm();
        video.play().catch(() => {});
      };
      const leave = () => {
        video.pause();
        panel.classList.remove('is-playing');
        // Rewind so the next hover starts from the top of the shot.
        try { video.currentTime = 0; } catch (_) {}
      };

      panel.addEventListener('pointerenter', enter);
      panel.addEventListener('pointerleave', leave);
      panel.addEventListener('focus', enter);
      panel.addEventListener('blur', leave);
    });
  };

  /* ---- Anchor navigation -------------------------------------
     Horizon scrolls an inner .page-wrapper rather than the document, and a
     fragment link only ever scrolls the document. The result is that a link
     to #an-object does nothing at all when you are already on the page it
     points into — which is precisely the header dropdown's job now that each
     line page carries its objects. The same was true of STORY on the
     homepage.

     So the fragment is resolved against the real scroller. Three entry
     points, because a fragment can arrive three ways: as a click (which may
     not change the hash at all, if it is already that hash), as a hash
     change, and as a page load. The load case also corrects the browser's
     own attempt, which lands short when the scroller is not the document.

     If the page scrolls normally — any other theme, any other layout — this
     does nothing and the browser keeps handling anchors itself. */
  const anchors = () => {
    const scroller = document.querySelector('.page-wrapper');
    if (!scroller || scroller.scrollHeight <= scroller.clientHeight + 1) return;
    if (anchors.armed) return;
    anchors.armed = true;

    /* The travel is animated by hand.

       .page-wrapper computes scroll-behavior: smooth, and on this container
       the smooth animation simply never runs — scrollTo({behavior:'smooth'})
       and a plain scrollTop assignment both leave it exactly where it was,
       while behavior:'instant' moves it. So a fragment link either did
       nothing or teleported four thousand pixels.

       Stepping it per frame with instant writes sidesteps the whole question:
       it is the one mode the container is known to honour, and the easing is
       ours rather than the browser's. Reduced motion gets the jump. */
    let token = 0;
    let seized = false;

    const goTo = (target) => {
      const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      const to = Math.max(0, scroller.scrollTop + target.getBoundingClientRect().top - margin);
      const from = scroller.scrollTop;
      const delta = to - from;

      const land = () => {
        // Send the keyboard where the eye went, without provoking a second scroll.
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      };

      const mine = ++token;

      if (reduced.matches || Math.abs(delta) < 2) {
        scroller.scrollTo({ top: to, behavior: 'instant' });
        land();
        return;
      }

      // Long journeys take a little longer, but not proportionally so.
      const duration = Math.min(950, Math.max(340, Math.abs(delta) * 0.32));
      const started = performance.now();
      const ease = (x) => 1 - Math.pow(1 - x, 3);

      /* Arrival must not depend on the animation running. requestAnimationFrame
         is throttled to nothing in a background tab, and a reader who opens a
         link in one and comes back later should find the object, not the top of
         the page. If the frames never came, this puts them there. */
      const watchdog = setTimeout(() => {
        if (mine !== token) return;
        if (Math.abs(scroller.scrollTop - to) > 2) {
          scroller.scrollTo({ top: to, behavior: 'instant' });
        }
        land();
      }, duration + 400);

      const step = (now) => {
        // A newer jump, or the reader taking over, wins.
        if (mine !== token) { clearTimeout(watchdog); return; }
        const p = Math.min(1, (now - started) / duration);
        scroller.scrollTo({ top: from + delta * ease(p), behavior: 'instant' });
        if (p < 1) {
          requestAnimationFrame(step);
        } else {
          clearTimeout(watchdog);
          land();
        }
      };
      requestAnimationFrame(step);
    };

    // Any deliberate scroll from the reader cancels the animation rather than
    // fighting it for the scrollbar.
    const yield_ = () => { token++; seized = true; };
    scroller.addEventListener('wheel', yield_, { passive: true });
    scroller.addEventListener('touchstart', yield_, { passive: true });

    const fromHash = (hash) => {
      const raw = (hash || location.hash || '').slice(1);
      if (!raw) return null;
      let id = raw;
      try { id = decodeURIComponent(raw); } catch (_) { /* keep it raw */ }
      return document.getElementById(id);
    };

    document.addEventListener('click', (e) => {
      const link = e.target.closest && e.target.closest('a[href]');
      if (!link || link.target === '_blank') return;

      let url;
      try { url = new URL(link.href, location.href); } catch (_) { return; }
      if (url.pathname !== location.pathname || url.search !== location.search) return;
      if (!url.hash || url.hash === '#') return;

      const target = fromHash(url.hash);
      if (!target) return;

      e.preventDefault();
      goTo(target);
      // Keep the address bar honest, and keep Back working.
      if (url.hash !== location.hash) history.pushState(null, '', url.hash);
    });

    window.addEventListener('hashchange', () => {
      const target = fromHash();
      if (target) goTo(target);
    });

    /* On arrival the page is still assembling itself. Galleries, films and
       posters all take their height after DOMContentLoaded, so an object four
       thousand pixels down measures as sitting at the top until they do, and
       aiming once — immediately — travels nowhere and stays there.

       So the arrival watches the target rather than trusting it. While the
       target's offset is still moving, the layout has not finished and the
       journey is not worth making; once it holds still, it is made. Watching
       continues for a few seconds afterwards, because a film that decodes
       late moves everything below it, and re-aims only when the offset
       actually changes — never mid-travel, which would restart the easing
       every tick and crawl. The reader touching the scroller ends it. */
    if (location.hash) {
      const arrive = () => {
        const target = fromHash();
        if (!target) return;

        const deadline = performance.now() + 6000;
        let last = null;
        let aimed = null;

        const tick = () => {
          if (seized) return;
          const at = scroller.scrollTop + target.getBoundingClientRect().top;
          const settled = last !== null && Math.abs(at - last) < 1;
          last = at;

          if (settled && (aimed === null || Math.abs(at - aimed) > 1)) {
            aimed = at;
            goTo(target);
          }
          if (performance.now() < deadline) setTimeout(tick, 150);
        };

        tick();
      };

      /* Started on a timer rather than a frame. requestAnimationFrame is
         frozen outright in a hidden tab, so a reader opening one of these in
         the background and coming to it later would arrive at the top of the
         page — the very thing the watchdog above exists to prevent. The
         watching is all timers anyway; it does not need a frame to begin. */
      setTimeout(arrive, 0);
    }
  };

  const init = () => {
    reveal();
    failsafe();
    heroVideo();
    hoverVideo();
    anchors();
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init, { once: true });

  document.addEventListener('shopify:section:load', init);
})();
